import React, { useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { Amplify, API, Storage } from 'aws-amplify';
import amplifyconfig from '../../../../amplifyconfiguration.json';
import ReactDOM from 'react-dom'
import scrollToElement from "scroll-to-element"
import { Container } from "reactstrap"
import Text from "components/shared/Text"
import Button from "components/shared/Button"
import FormStyled from "components/shared/FormStyled"
import {
  validateRequired,
  validateEmail,
  validateFile
} from 'utils/Validations'
import TextField from "./TextField"
import DropdownField from "./DropdownField"
import CheckboxField from "./CheckboxField"
import RadioField from "./RadioField"
import FileUploadField from "./FileUploadField"
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

Amplify.configure(amplifyconfig);

const Loading = styled.span`
  display: inline-block;
  width: 35px;
  height: 35px;

  &:after {
    content: " ";
    display: block;
    width: 35px;
    height: 35px;
    margin-left: .5rem;
    border-radius: 50%;
    border: 6px solid #000;
    border-color: #000 transparent #000 transparent;
    animation: lds-dual-ring 1.2s linear infinite;
  }

  @keyframes lds-dual-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const ContactFormBuilder = ({ block }) => {
	const { executeRecaptcha } = useGoogleReCaptcha();
  const [state, setState] = useState({
    send_to: block.sendTo,
    email_subject: block.emailSubject,
    google_sheet_id: block.googleSheetId,
    fields: {},
    validate: {},
    required: {},
    googleVerified: '',
    sendingFormRequest: false,
    success: false
  })

  const formRef = useRef(null)
  const successMessageRef = useRef(null)

  useEffect(() => {
    setFieldState()
  }, [])

  const setFieldState = () => {
    let fields = {}
    let validate = {}
    let required = {}

    block.wpFields && block.wpFields.forEach((field) => {
      let name = field.name

      if (field.__typename === "WpTemplate_FeatureArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes") {
        fields[name] = []
        field.options.forEach((option, i) => {
          fields[name][i] = { label: field.label, value: option.label, isChecked: false }
        })
      } else {
        fields[name] = { label: field.label, value: "" }
      }
      validate[name] = ""
      if (field.required === "yes") required[name] = ""
    })

    setState(prevState => ({
      ...prevState,
      fields,
      validate,
      required
    }))
  }

  const handleChange = async (e) => {
    const target = e.target
    const name = target.name
    const value = target.value

    let required = { ...state.required }

    if (required.hasOwnProperty(name)) {
      required[name] = value
    }

    let fields = { ...state.fields }

    if (target.type === 'checkbox') {
      let hasValue = ""
      fields[name].forEach(field => {
        if (field.value === value) {
          field.isChecked = target.checked
          if (field.isChecked) {
            hasValue = true
          }
        }
      })
      if (required.hasOwnProperty(name)) {
        required[name] = hasValue
      }
    } else if (target.type === 'file') {
      fields[name].value = target.files[0]
      fields[name].name = target.files[0].name
      fields[name].type = "file"
      fields[name].size = target.files[0].size
      fields[name].base64 = await toBase64(target.files[0])
    } else {
      fields[name].value = value
    }

    fields[name].type = target.type

    setState(prevState => ({
      ...prevState,
      required,
      fields
    }))
  }

  const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })

  const handleSubmit = async (e) => {
    e.preventDefault()

    setState(prevState => ({ ...prevState, sendingFormRequest: true }))

    let formData = { ...state }
    let { validate } = state
    let errors = false

    for (let key in state.validate) {
      if (state.required[key] === "" && key !== "googleVerified" && key !== "googleVerifiedErrMsg") {
        validate[key] = 'has-danger'
        errors = true
      }
    }

    setState(prevState => ({ ...prevState, validate }))

    if (errors) {
      setState(prevState => ({ ...prevState, sendingFormRequest: false }))
      return
    }

    for (let key in formData.fields) {
      if (formData.fields[key].type !== undefined && formData.fields[key].type === "file") {
        if (formData.fields[key].size / 1048576 > 2) {
          validate[key] = 'has-danger'
          errors = true
          setState(prevState => ({ ...prevState, validate }))
        } else {
          try {
            const result = await Storage.put(Date.now() + formData.fields[key].name, formData.fields[key].value)
            const fileUrl = process.env.GATSBY_BACKEND_URL + '/admin.php?page=s3_bucket&key=' + result.key
            formData.fields[key] = { label: "File Upload", value: fileUrl, type: "text" }
          } catch (error) {
            validate[key] = 'has-danger'
            errors = true
            setState(prevState => ({ ...prevState, validate }))
          }
        }
      }
    }

    if (errors) return

    let apiName = 'hamptonCourtPalaceApi'
    let path = "/contact"
    let myInit = {
      body: formData,
      headers: {},
      response: true
    }

    return API.post(apiName, path, myInit).then(response => {
      setState(prevState => ({ ...prevState, sendingFormRequest: false }))

      if (response.status === 200 && response.data.result === "success") {
        const domNode = ReactDOM.findDOMNode(successMessageRef.current)

        setState({
          success: true,
          fields: {}
        })

        setFieldState()

        scrollToElement(domNode, {
          offset: -120,
          duration: 500
        })
      }
    }).catch(error => {
      setState(prevState => ({ ...prevState, sendingFormRequest: false }))
      return error.response
    })
  }

  const { wpFields, requiredFieldsText, submitText, thankYouMessage } = block

  return (
    <>
      <Container className="py-4" fluid>
        <FormStyled onSubmit={(e) => handleSubmit(e)} noValidate ref={formRef}>
          {
            wpFields && wpFields.map((field, i) => {
              switch (field.__typename) {
                case "WpPage_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Text":
                case "WpTemplate_FeatureArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Text":
                case 'WpTastemenu_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Text':
                case 'WpRecipe_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Text':
                case 'WpArtisanProducers_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Text':
                case 'WpNewsArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Text':
                  return (
                    <TextField
                      key={i}
                      field={field}
                      state={state}
                      handleChange={handleChange}
                      validateRequired={validateRequired}
                    />
                  )

                case "WpPage_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Dropdown":
                case "WpTemplate_FeatureArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Dropdown":
                case 'WpTastemenu_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Dropdown':
                case 'WpRecipe_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Dropdown':
                case 'WpArtisanProducers_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Dropdown':
                case 'WpNewsArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Dropdown':
                  return (
                    <DropdownField
                      key={i}
                      field={field}
                      state={state}
                      handleChange={handleChange}
                      validateRequired={validateRequired}
                    />
                  )

                case "WpPage_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes":
                case "WpTemplate_FeatureArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes":
                case 'WpTastemenu_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes':
                case 'WpRecipe_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes':
                case 'WpArtisanProducers_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes':
                case 'WpNewsArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Checkboxes':
                  return (
                    <CheckboxField
                      key={i}
                      field={field}
                      state={state}
                      handleChange={handleChange}
                      validateRequired={validateRequired}
                    />
                  )

                case "WpPage_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Radio":
                case "WpTemplate_FeatureArticle_cftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Radio":
                case 'WpTastemenu_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Radio':
                case 'WpRecipe_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Radio':
                case 'WpArtisanProducers_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Radio':
                case 'WpNewsArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_Radio':
                  return (
                    <RadioField
                      key={i}
                      field={field}
                      state={state}
                      handleChange={handleChange}
                      validateRequired={validateRequired}
                    />
                  )

                case "WpPage_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_FileUpload":
                case "WpTemplate_FeatureArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_FileUpload":
                case 'WpTastemenu_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_FileUpload':
                case 'WpRecipe_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_FileUpload':
                case 'WpArtisanProducers_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_FileUpload':
                case 'WpNewsArticle_Acftwocolumnflexiblecontent_TwoColumnBlocks_ContactFormBuilder_Fields_FileUpload':
                  return (
                    <FileUploadField
                      key={i}
                      field={field}
                      state={state}
                      handleChange={handleChange}
                      validateFile={validateFile}
                      validateRequired={validateRequired}
                    />
                  )

                default:
                  return ""
              }
            })
          }

          <Text sm bold>* {requiredFieldsText}</Text>

          <div className="d-flex align-items-center">
            <Button
              as="button"
              color="primary"
              className="my-3"
              type="submit"
              disabled={state.sendingFormRequest}
            >
              <span>
                {state.sendingFormRequest ? ("Sending...") : (<>{submitText}</>)}
              </span>
            </Button>
            {state.sendingFormRequest &&
              <Loading />
            }
          </div>

        </FormStyled>
      </Container>
      {state.success &&
        <Container className="py-4" fluid>
          <div dangerouslySetInnerHTML={{ __html: thankYouMessage }} ref={successMessageRef} />
        </Container>
      }
    </>
  )
}

export default ContactFormBuilder